import { Form, Formik } from "formik";

import { Box, Button, CircularProgress, Tooltip, Typography } from "@mui/material";

import { CampaignAnalyticsService } from "@analytics/index";

import NylasApi from "@api/nylas.api";

import EmailConnected from "@assets/images/campaigns/email_connected.gif";
import EmailIntegration from "@assets/images/campaigns/email_integration.svg";
import Gmail from "@assets/images/campaigns/gmail.svg";
import Icloud from "@assets/images/campaigns/icloud.svg";
import More from "@assets/images/campaigns/more.svg";
import Outlook from "@assets/images/campaigns/outlook.svg";
import Yahoo from "@assets/images/campaigns/yahoo.svg";

import TextField from "@components/Forms/FormInputs/TextField";

import { IUser } from "@interfaces/user.type";

import userService from "@services/user.service";

import { usePopupActions } from "@store/popupStore";
import { useUserStore } from "@store/userStore";

declare global {
  function handlePopups(): void;
  function updateUserData(user: IUser): Promise<void> | void;
}

export default function ConnectEmailAccountForm() {
  const { actions, ...currentUser } = useUserStore();
  const popupActions = usePopupActions();

  /**
   * Opens a new window for Nylas authentication and allows that window to refresh the user in this window
   *
   * @param email - User's email address for authentication.
   */

  const loginUser = async (email: string) => {
    const authUrl = await NylasApi.generateAuthUrl(email, "/nylas/callback");

    const windowFeatures = "width=800,height=600,resizable=yes,scrollbars=yes,status=yes";

    globalThis.updateUserData = async (user) => {
      await userService.set({ ...currentUser, ...user });
    };

    const messageContent = globalThis.location.href.includes("campaigns/create")
      ? "You can now send your Campaign!"
      : "You can now send Campaigns from your Portfolio.";

    globalThis.handlePopups = () => {
      popupActions.closeModal();
      popupActions.openModal({
        content: (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              px: 4,
              py: 2,
            }}
          >
            <img alt="Email Connected Graphic" src={EmailConnected} width="160" />
            <Typography variant="h4">Success!</Typography>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body1">Your email account has been connected.</Typography>
              <Typography variant="body1">{messageContent}</Typography>
            </Box>
          </Box>
        ),
        title: "Email Account Connected",
      });
      if (currentUser.id) {
        CampaignAnalyticsService.addConnectedEmailAccount(currentUser.id);
      }
    };

    // Make sure to remove the function from the window object when the window is closed
    window.addEventListener("beforeunload", () => {
      globalThis.handlePopups = () => {
        /* Set to empty function because can't delete non optional type properties */
      };
      globalThis.updateUserData = () => {
        /* Set to empty function because can't delete non optional type properties */
      };
    });
    window.open(authUrl, "AuthWindow", windowFeatures);
  };

  return (
    <Formik
      initialValues={{ email: "" }}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await loginUser(values.email);
        } catch {
          setSubmitting(false);
        }
      }}
    >
      {({ dirty, isSubmitting, isValid }) => (
        <Box
          component={Form}
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            px: 2,
            py: 1,
          }}
        >
          <img alt="Email Integration Graphic" src={EmailIntegration} width="104" />
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 2,
              justifyContent: "center",
            }}
          >
            <img alt="Gmail Logo" src={Gmail} width="56" />
            <img alt="Outlook Logo" src={Outlook} width="56" />
            <img alt="Icloud Logo" src={Icloud} width="56" />
            <img alt="Yahoo Logo" src={Yahoo} width="56" />
            <Tooltip title="Connect with Gmail, Outlook, Exchange, Office 365, iCloud, and more for seamless emailing.">
              <img alt="More Providers Graphic" src={More} style={{ cursor: "help" }} width="96" />
            </Tooltip>
          </Box>
          <Typography sx={{ mt: 4 }} variant="body1">
            <strong>Start sending outreach Campaigns from your organization email.</strong>
          </Typography>
          <TextField color="secondary" isFullWidth isRequired label="Email" name="email" type="email" />
          <Button
            disabled={!isValid || !dirty || isSubmitting}
            fullWidth
            size="large"
            sx={{ mt: 1 }}
            type="submit"
            variant="contained"
          >
            {isSubmitting ? <CircularProgress size={20} /> : "Connect email"}
          </Button>
        </Box>
      )}
    </Formik>
  );
}
